import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import { useAuth } from "../hooks/useAuth";
import Card from "react-bootstrap/esm/Card";
import Button from "react-bootstrap/esm/Button";
import Alert from "react-bootstrap/esm/Alert";

/**
 * Transition to the superadmin claim (GOD-42): shows whether this session's
 * token carries `superadmin: true` and sets it on the own account.
 */
export default function SuperadminClaim() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [hasClaim, setHasClaim] = useState<boolean | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    async function check() {
        const result = await user?.getIdTokenResult(true);
        setHasClaim(result?.claims.superadmin === true);
    }

    async function claim() {
        setBusy(true);
        setError("");
        try {
            await httpsCallable(functions, "claimSuperadmin")({});
            await check();
        } catch (err: any) {
            setError(err?.message ?? t("errors.anErrorOccurred"));
        }
        setBusy(false);
    }

    return (
        <Card className="mb-4" data-testid="superadmin-claim">
            <Card.Body>
                <Card.Title as="h5">{t("superadmin.claimTitle")}</Card.Title>
                <p className="small text-muted">{t("superadmin.claimIntro")}</p>
                {hasClaim === true && <Alert variant="success" className="small">{t("superadmin.claimPresent")}</Alert>}
                {hasClaim === false && <Alert variant="warning" className="small">{t("superadmin.claimMissing")}</Alert>}
                {error && <Alert variant="danger" className="small">{error}</Alert>}
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={check} disabled={busy}>
                        {t("superadmin.claimCheck")}
                    </Button>
                    <Button size="sm" onClick={claim} disabled={busy || hasClaim === true}>
                        {t("superadmin.claimSet")}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
}
